import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.ts";
import { AlertBanner, ConfirmDialog, EmptyState } from "../components/ui.tsx";
import { useAsync } from "../hooks.ts";
import { FREQUENCIES, FREQ_DAYS, FREQ_LABEL, type Freq } from "../../shared/schedule.ts";
import { ZONES, ZONE_LABEL } from "../../shared/trips.ts";
import type { FloorView, RoomView, TaskView, Trip } from "../../shared/types.ts";

/**
 * Personal tasks arrive expanded — one copy per kid — because that is what the
 * ticking pages need. Here the task itself is the thing being edited, so the
 * copies fold back into one row.
 */
function uniqueTasks(tasks: TaskView[]): TaskView[] {
  return tasks.filter((task, index) => tasks.findIndex((other) => other.id === task.id) === index);
}

/**
 * Where the house gets shaped — /admin, reachable only by URL or from behind
 * your own name.
 *
 * Kept off the daily navigation on purpose. Everything here is rare and
 * consequential: renaming a room, retiring a job, deleting a floor and its
 * whole history. Sitting it next to Today invites someone to wander in
 * mid-week; keeping it out means the app people actually use every day has
 * three screens and no settings.
 *
 * Two save idioms, chosen by control type: discrete selectors (frequency,
 * floor, kid job) autosave on change; free text waits for an explicit Save so
 * typing never fires requests.
 */
export function Admin() {
  const { data, error, loading, reload } = useAsync(() => api.house());
  const people = useAsync(() => api.people());
  const recent = useAsync(() => api.scoreboard(0));
  const [pending, setPending] = useState<null | { kind: "floor" | "room"; id: number; name: string }>(null);
  const [failure, setFailure] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>) {
    try {
      await action();
      setFailure(null);
      reload();
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : "That didn’t save");
    }
  }

  if (error) return <AlertBanner>Couldn’t load the house. {error}</AlertBanner>;
  if (loading && !data) return <p className="muted">Loading…</p>;
  if (!data) return null;

  return (
    <div className="admin">
      <header className="admin__head">
        <div>
          <span className="eyebrow">Admin</span>
          <h1 className="admin__title">Set up the house</h1>
        </div>
        <Link className="btn btn--quiet" to="/">
          Done
        </Link>
      </header>

      <p className="head__note" style={{ marginBottom: "var(--space-6)" }}>
        Rename anything, move rooms between floors, add and retire jobs. Keep it to about five jobs a room — a list
        nobody can finish stops being a plan.
      </p>

      {failure ? <AlertBanner>{failure}</AlertBanner> : null}

      {data.floors.map((floor) => (
        <FloorEditor
          key={floor.id}
          floor={floor}
          floors={data.floors}
          onRun={run}
          onDelete={(target) => setPending(target)}
        />
      ))}

      <section className="admin__group">
        <div className="section-head">
          <span className="eyebrow">Add a floor</span>
        </div>
        <NameForm
          placeholder="Loft, cellar, garage…"
          label="Add floor"
          onSubmit={(name) => run(() => api.createFloor(name))}
        />
      </section>

      <Trips onRun={run} />

      <Rotting floors={data.floors} onRun={run} />

      <section className="admin__group">
        <div className="section-head">
          <span className="eyebrow">Correct a mistake</span>
        </div>
        <p className="head__note" style={{ margin: "0 0 var(--space-4)" }}>
          A tick is undoable for ten seconds where it was made; after that it stands, and taking it back is a deliberate
          act here. It lives here rather than on the kitchen board because that board is one shared session — anyone
          walking past it could otherwise undo anyone.
        </p>
        <div className="panel">
          {(recent.data?.recent ?? []).length === 0 ? (
            <p className="muted">Nothing done yet this week.</p>
          ) : (
            (recent.data?.recent ?? []).map((entry) => (
              <div key={entry.id} className="admin__row">
                <span className="admin__rowname">
                  <span aria-hidden="true">{entry.emoji}</span> {entry.task}
                  <span className="muted">
                    {" "}
                    — {entry.person ?? "uncredited"}, {new Date(entry.doneAt).toLocaleString()}
                  </span>
                </span>
                <button
                  className="btn btn--quiet"
                  onClick={() => run(() => api.undoCompletion(entry.id).then(() => recent.reload()))}
                >
                  Take it back
                </button>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="admin__group">
        <div className="section-head">
          <span className="eyebrow">Who lives here</span>
        </div>
        <div className="panel">
          {(people.data ?? []).map((person) => (
            <div key={person.id} style={{ marginBottom: "var(--space-4)" }}>
              <NameForm
                initial={person.name}
                label="Save"
                prefix={person.emoji}
                hint={person.kind === "kid" ? "Kid" : person.kind === "guest" ? "Guest" : "Parent"}
                onSubmit={(name) => run(() => api.updatePerson(person.id, { name }).then(() => people.reload()))}
              />
              {person.kind === "kid" ? <PinForm person={person} onRun={run} onSaved={() => people.reload()} /> : null}
            </div>
          ))}
        </div>
      </section>

      {pending ? (
        <ConfirmDialog
          title={`Delete ${pending.name}?`}
          body={
            pending.kind === "floor"
              ? "This removes the floor, every room on it, all their jobs and the record of everything ever done in them. There is no undo."
              : "This removes the room, its jobs and the record of everything ever done in it. There is no undo."
          }
          confirmLabel="Delete"
          requireTypedConfirmation={pending.name}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const target = pending;
            setPending(null);
            void run(() => (target.kind === "floor" ? api.deleteFloor(target.id) : api.deleteRoom(target.id)));
          }}
        />
      ) : null}
    </div>
  );
}

function FloorEditor({
  floor,
  floors,
  onRun,
  onDelete,
}: {
  floor: FloorView;
  floors: FloorView[];
  onRun: (action: () => Promise<unknown>) => Promise<void>;
  onDelete: (target: { kind: "floor" | "room"; id: number; name: string }) => void;
}) {
  return (
    <section className="admin__group">
      <div className="section-head">
        <span className="eyebrow">{floor.name}</span>
      </div>
      <div className="panel" style={{ marginBottom: "var(--space-3)" }}>
        <div className="btn-row" style={{ alignItems: "flex-end" }}>
          <NameForm
            initial={floor.name}
            label="Rename floor"
            onSubmit={(name) => onRun(() => api.updateFloor(floor.id, { name }))}
          />
          <button
            className="btn btn--danger"
            onClick={() => onDelete({ kind: "floor", id: floor.id, name: floor.name })}
          >
            Delete floor
          </button>
        </div>
      </div>

      {floor.rooms.map((room) => (
        <RoomEditor key={room.id} room={room} floors={floors} onRun={onRun} onDelete={onDelete} />
      ))}

      <div className="panel" style={{ marginTop: "var(--space-3)" }}>
        <NameForm
          placeholder="New room name"
          label="Add room"
          onSubmit={(name) => onRun(() => api.createRoom({ floorId: floor.id, name }))}
        />
      </div>
    </section>
  );
}

function RoomEditor({
  room,
  floors,
  onRun,
  onDelete,
}: {
  room: RoomView;
  floors: FloorView[];
  onRun: (action: () => Promise<unknown>) => Promise<void>;
  onDelete: (target: { kind: "floor" | "room"; id: number; name: string }) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="panel" style={{ marginBottom: "var(--space-3)" }}>
      <div className="btn-row" style={{ alignItems: "center", justifyContent: "space-between" }}>
        <strong>
          <span aria-hidden="true">{room.emoji} </span>
          {room.name}
        </strong>
        <button className="btn btn--quiet" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {open ? "Close" : `Edit · ${room.tasks.length} jobs`}
        </button>
      </div>

      {open ? (
        <div style={{ marginTop: "var(--space-4)" }}>
          <div className="form-grid">
            <NameForm
              initial={room.name}
              label="Save name"
              onSubmit={(name) => onRun(() => api.updateRoom(room.id, { name }))}
            />
            <NameForm
              initial={room.emoji}
              label="Save emoji"
              onSubmit={(emoji) => onRun(() => api.updateRoom(room.id, { emoji }))}
            />
            <label className="field">
              <span className="field__label">Floor</span>
              <select
                value={room.floorId}
                onChange={(event) => onRun(() => api.updateRoom(room.id, { floorId: Number(event.target.value) }))}
              >
                {floors.map((floor) => (
                  <option key={floor.id} value={floor.id}>
                    {floor.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Where this happens</span>
              <select
                value={room.zone}
                title="At home pauses on trips; Travelling pauses at home; Anywhere never pauses"
                onChange={(event) => onRun(() => api.updateRoom(room.id, { zone: event.target.value }))}
              >
                {ZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {ZONE_LABEL[zone]}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="section-head">
            <span className="eyebrow">Jobs</span>
          </div>
          {room.tasks.length === 0 ? (
            <EmptyState icon="🧽" title="No jobs in this room" />
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {uniqueTasks(room.tasks).map((task) => (
                <TaskEditor key={task.id} task={task} onRun={onRun} />
              ))}
            </ul>
          )}

          <NewTaskForm roomId={room.id} onRun={onRun} />

          <div className="btn-row" style={{ marginTop: "var(--space-5)" }}>
            <button
              className="btn btn--danger"
              onClick={() => onDelete({ kind: "room", id: room.id, name: room.name })}
            >
              Delete room
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TaskEditor({ task, onRun }: { task: TaskView; onRun: (action: () => Promise<unknown>) => Promise<void> }) {
  return (
    <li className="admin__row">
      <div className="admin__rowname">
        <NameForm
          initial={task.title}
          label="Save"
          onSubmit={(title) => onRun(() => api.updateTask(task.id, { title }))}
        />
      </div>
      <label className="field">
        <span className="field__label">Every</span>
        <select
          value={task.freq}
          onChange={(event) => onRun(() => api.updateTask(task.id, { freq: event.target.value }))}
        >
          {FREQUENCIES.map((freq) => (
            <option key={freq} value={freq}>
              {FREQ_LABEL[freq]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field__label">Minutes</span>
        <input
          type="number"
          min={1}
          defaultValue={task.effortMin}
          style={{ width: "5rem" }}
          onBlur={(event) => {
            const value = Number(event.target.value);
            if (value > 0 && value !== task.effortMin) void onRun(() => api.updateTask(task.id, { effortMin: value }));
          }}
        />
      </label>
      <button
        type="button"
        className="segmented__option"
        aria-pressed={task.kidFriendly}
        title="A six-year-old can finish this alone"
        onClick={() => onRun(() => api.updateTask(task.id, { kidFriendly: !task.kidFriendly }))}
      >
        🧒 Kid job
      </button>
      <button
        type="button"
        className="segmented__option"
        aria-pressed={task.personal}
        title="Everyone does their own — each kid gets their own copy to tick"
        onClick={() => onRun(() => api.updateTask(task.id, { personal: !task.personal }))}
      >
        🙋 Each their own
      </button>
      <button className="btn btn--quiet" onClick={() => onRun(() => api.archiveTask(task.id))}>
        Retire
      </button>
    </li>
  );
}

function NewTaskForm({ roomId, onRun }: { roomId: number; onRun: (action: () => Promise<unknown>) => Promise<void> }) {
  const [title, setTitle] = useState("");
  const [freq, setFreq] = useState<Freq>("weekly");
  const [effortMin, setEffortMin] = useState(10);
  const [kidFriendly, setKidFriendly] = useState(false);
  const [personal, setPersonal] = useState(false);

  return (
    <form
      className="form-grid"
      style={{ marginTop: "var(--space-4)", alignItems: "end" }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!title.trim()) return;
        void onRun(() => api.createTask({ roomId, title, freq, effortMin, kidFriendly, personal })).then(() =>
          setTitle(""),
        );
      }}
    >
      <label className="field">
        <span className="field__label">New job</span>
        <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Windows wiped" />
      </label>
      <label className="field">
        <span className="field__label">Every</span>
        <select value={freq} onChange={(event) => setFreq(event.target.value as Freq)}>
          {FREQUENCIES.map((option) => (
            <option key={option} value={option}>
              {FREQ_LABEL[option]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field__label">Minutes</span>
        <input type="number" min={1} value={effortMin} onChange={(event) => setEffortMin(Number(event.target.value))} />
      </label>
      <button
        type="button"
        className="segmented__option"
        aria-pressed={kidFriendly}
        onClick={() => setKidFriendly((value) => !value)}
      >
        🧒 Kid job
      </button>
      <button
        type="button"
        className="segmented__option"
        aria-pressed={personal}
        title="Everyone does their own — each kid gets their own copy to tick"
        onClick={() => setPersonal((value) => !value)}
      >
        🙋 Each their own
      </button>
      <button className="btn btn--ghost" type="submit" disabled={!title.trim()}>
        Add job
      </button>
    </form>
  );
}

/** Free text with an explicit Save, disabled until the value actually changes. */
function NameForm({
  initial = "",
  label,
  placeholder,
  prefix,
  hint,
  onSubmit,
}: {
  initial?: string;
  label: string;
  placeholder?: string;
  prefix?: string;
  hint?: string;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  const dirty = value.trim() !== initial.trim() && value.trim().length > 0;

  return (
    <form
      className="btn-row"
      style={{ alignItems: "flex-end" }}
      onSubmit={(event) => {
        event.preventDefault();
        if (dirty) onSubmit(value.trim());
      }}
    >
      <label className="field" style={{ flex: 1 }}>
        {hint ? <span className="field__label">{hint}</span> : null}
        <input
          value={value}
          placeholder={placeholder}
          onChange={(event) => setValue(event.target.value)}
          aria-label={label}
        />
      </label>
      <button className="btn btn--ghost" type="submit" disabled={!dirty}>
        {prefix ? `${prefix} ` : ""}
        {label}
      </button>
    </form>
  );
}

/**
 * The list worth acting on.
 *
 * After a few weeks, some jobs will have been done ten times and some never
 * once. The never-once list is the most useful thing this system produces,
 * because the right response is almost always to delete them rather than to
 * try harder. A chore list nobody can finish stops being a plan and becomes an
 * accusation.
 */
function Rotting({ floors, onRun }: { floors: FloorView[]; onRun: (action: () => Promise<unknown>) => Promise<void> }) {
  const stale = floors
    .flatMap((floor) => floor.rooms)
    .flatMap((room) => uniqueTasks(room.tasks).map((task) => ({ task, room })))
    // Paused work is not rotting, it is waiting — a travel job in February
    // does not belong on a list of things nobody will ever do.
    .filter(({ task }) => !task.paused && (task.status === "never" || task.daysLate >= FREQ_DAYS[task.freq]))
    .sort((a, b) => b.task.daysLate - a.task.daysLate || a.task.title.localeCompare(b.task.title));

  return (
    <section className="admin__group">
      <div className="section-head">
        <span className="eyebrow">Never getting done · {stale.length}</span>
      </div>
      <p className="head__note" style={{ margin: "0 0 var(--space-4)" }}>
        Jobs nobody has ever done, or that have slipped by more than a full cycle. Retiring one is not giving up — a
        list nobody can finish stops being a plan.
      </p>
      <div className="panel">
        {stale.length === 0 ? (
          <p className="muted">Nothing is rotting. The list matches the house.</p>
        ) : (
          stale.slice(0, 25).map(({ task, room }) => (
            <div key={task.id} className="admin__row">
              <span className="admin__rowname">
                {task.title}
                <span className="muted">
                  {" "}
                  — {room.emoji} {room.name} · {task.status === "never" ? "never done" : `${task.daysLate}d late`}
                </span>
              </span>
              <button className="btn btn--quiet" onClick={() => onRun(() => api.archiveTask(task.id))}>
                Retire
              </button>
            </div>
          ))
        )}
        {stale.length > 25 ? <p className="muted">and {stale.length - 25} more</p> : null}
      </div>
    </section>
  );
}

/**
 * Trips: two dates, and everything else follows.
 *
 * While today is inside a trip, home rooms pause and travel rooms wake. The
 * dates can be written weeks ahead — the switch happens by itself on the
 * morning you leave, which is exactly the morning nobody would remember to
 * flip one.
 */
function Trips({ onRun }: { onRun: (action: () => Promise<unknown>) => Promise<void> }) {
  const { data, reload } = useAsync(() => api.trips());
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [pending, setPending] = useState<Trip | null>(null);
  const trips = data ?? [];
  const ready = name.trim().length > 0 && startsOn !== "" && endsOn !== "" && startsOn <= endsOn;

  return (
    <section className="admin__group">
      <div className="section-head">
        <span className="eyebrow">Trips</span>
      </div>
      <p className="head__note" style={{ margin: "0 0 var(--space-4)" }}>
        While a trip is on, home rooms pause — nothing piles up while nobody is there to do it — and the travel jobs
        take over. Dates include both ends.
      </p>
      <div className="panel">
        {trips.length === 0 ? (
          <p className="muted">No trips on the calendar.</p>
        ) : (
          trips.map((trip) => (
            <div key={trip.id} className="admin__row">
              <span className="admin__rowname">
                <span aria-hidden="true">{trip.emoji}</span> {trip.name}
                <span className="muted">
                  {" "}
                  — {trip.startsOn} to {trip.endsOn}
                </span>
              </span>
              <button className="btn btn--quiet" onClick={() => setPending(trip)}>
                Remove
              </button>
            </div>
          ))
        )}

        <form
          className="form-grid"
          style={{ marginTop: "var(--space-4)", alignItems: "end" }}
          onSubmit={(event) => {
            event.preventDefault();
            if (!ready) return;
            void onRun(() => api.createTrip({ name: name.trim(), startsOn, endsOn }).then(() => reload())).then(() => {
              setName("");
              setStartsOn("");
              setEndsOn("");
            });
          }}
        >
          <label className="field">
            <span className="field__label">New trip</span>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Summer in the Cape" />
          </label>
          <label className="field">
            <span className="field__label">From</span>
            <input type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Until</span>
            <input type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} />
          </label>
          <button className="btn btn--ghost" type="submit" disabled={!ready}>
            Add trip
          </button>
        </form>
      </div>

      {pending ? (
        <ConfirmDialog
          title={`Remove ${pending.name}?`}
          body="Removing a trip also removes the pause it gave the house: home jobs get those days back on their clocks, and a past trip's days count as if everyone had been home."
          confirmLabel="Remove"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const target = pending;
            setPending(null);
            void onRun(() => api.deleteTrip(target.id).then(() => reload()));
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * A child's own PIN — set here, typed by them at the tablet.
 *
 * Per child rather than shared, so the login itself says who arrived. The
 * server stores only a hash; there is nothing to display back, which is why
 * this form only ever sets.
 */
function PinForm({
  person,
  onRun,
  onSaved,
}: {
  person: { id: number; name: string; hasPin?: boolean };
  onRun: (action: () => Promise<unknown>) => Promise<void>;
  onSaved: () => void;
}) {
  const [pin, setPin] = useState("");
  const valid = /^\d{4,8}$/.test(pin);

  return (
    <form
      className="btn-row"
      style={{ alignItems: "flex-end", marginTop: "var(--space-2)" }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        void onRun(() => api.setKidPin(person.id, pin)).then(() => {
          setPin("");
          onSaved();
        });
      }}
    >
      <label className="field" style={{ flex: 1 }}>
        <span className="field__label">{person.hasPin ? "Change PIN" : "Set PIN — needed to sign in"}</span>
        <input
          value={pin}
          onChange={(event) => setPin(event.target.value)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="4–8 digits"
        />
      </label>
      <button className="btn btn--ghost" type="submit" disabled={!valid}>
        {person.hasPin ? "Change" : "Set"}
      </button>
    </form>
  );
}
